/**
 * Hook for evaluating navigation items with rule-based rendering.
 *
 * Takes a navigation configuration and evaluates all rules against
 * the current user/organization/permission context.
 *
 * Returns filtered navigation items that should be visible to the current user.
 */

import React, { useMemo, useState, useEffect } from 'react';
import type { NavItemConfig } from '../config/navigation';
import type { NavigationContext, EvaluatedNavItem } from '../lib/navigation/rules/NavigationRule';
import { useNavigationContext } from './useNavigationContext';
import { useCurrentOrg } from './useCurrentOrg';
import { useLocation } from 'react-router-dom';
import { logger } from '../lib/utils/logger';

const FETCH_BADGE_INTERVAL_MS = 60_000;

/**
 * Evaluate navigation items based on current context.
 *
 * @param config - Navigation configuration with rules
 * @returns Filtered and evaluated navigation items
 */
export function useNavigationItems(config: NavItemConfig[]) {
  const { currentOrg } = useCurrentOrg();
  const { context: baseContext, subscriptionLoading, authLoading } = useNavigationContext();
  const location = useLocation();
  const [fetchedCounts, setFetchedCounts] = useState<Record<string, number>>({});

  const context = useMemo(
    () => ({ ...baseContext, currentPath: location.pathname }),
    [baseContext, location.pathname],
  );

  const evaluatedItems = useMemo(
    () => config.map((item) => evaluateNavItem(item, context)).filter((item) => item.visible),
    [config, context],
  );

  // Sync counts (getCount + static count) recompute cheaply with context.
  // Async fetchCount runs on its own schedule and is merged in below.
  const syncCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of config) {
      const cfg = item.badgeConfig;
      if (!cfg) continue;
      if (cfg.getCount) counts[item.label] = cfg.getCount(context);
      else if (cfg.count !== undefined) counts[item.label] = cfg.count;
    }
    return counts;
  }, [config, context]);

  // Async fetches keyed only on org id — context churn shouldn't restart the
  // 60s polling timer or trigger duplicate network calls.
  useEffect(() => {
    const itemsWithFetch = config.filter((item) => item.badgeConfig?.fetchCount);
    if (itemsWithFetch.length === 0) return;

    let cancelled = false;
    const orgId = currentOrg?.id ?? null;

    const refresh = async () => {
      const next: Record<string, number> = {};
      for (const item of itemsWithFetch) {
        const fetchCount = item.badgeConfig?.fetchCount;
        if (!fetchCount) continue;
        try {
          next[item.label] = await fetchCount(orgId);
        } catch (error) {
          logger.error(`Failed to fetch badge count for ${item.label}`, error);
          next[item.label] = 0;
        }
      }
      if (cancelled) return;
      setFetchedCounts((prev) => (shallowEqualNumbers(prev, next) ? prev : next));
    };

    void refresh();
    const interval = setInterval(() => void refresh(), FETCH_BADGE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [config, currentOrg?.id]);

  const badgeCounts = useMemo(
    () => ({ ...fetchedCounts, ...syncCounts }),
    [fetchedCounts, syncCounts],
  );

  const loading = authLoading || (!!currentOrg && subscriptionLoading);

  return {
    items: evaluatedItems,
    badgeCounts,
    loading,
  };
}

/** Shallow-equal for `Record<string, number>` so we can skip no-op state updates. */
function shallowEqualNumbers(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => a[k] === b[k]);
}

/**
 * Evaluate a single nav item against navigation context.
 * Recursively evaluates nested items.
 *
 * @param item - Nav item configuration
 * @param context - Navigation context
 * @returns Evaluated nav item with computed visibility/state
 */
function evaluateNavItem(item: NavItemConfig, context: NavigationContext): EvaluatedNavItem {
  const visible = item.visibilityRules?.every((rule) => rule.isVisible(context)) ?? true;
  const clickable =
    item.clickableRules?.every((rule) => rule.isClickable?.(context) ?? true) ?? true;
  const enabled = item.enabledRules?.every((rule) => rule.isEnabled?.(context) ?? true) ?? true;

  const evaluatedNestedItems = item.items
    ? item.items.map((nestedItem) => evaluateNavItem(nestedItem, context)).filter((i) => i.visible)
    : undefined;

  const IconComponent = item.icon;
  const iconElement = React.createElement(IconComponent, { size: 20 });

  return {
    label: item.label,
    path: item.path,
    icon: iconElement,
    items: evaluatedNestedItems,
    visible,
    clickable,
    enabled,
    badgeCount: item.badgeConfig?.count,
    badgeText: item.badgeConfig?.text,
    // Default variant/color preserved for items without a badgeConfig override.
    badgeVariant: item.badgeConfig?.variant ?? 'count',
    badgeColor: item.badgeConfig?.color ?? 'orange',
  };
}
